import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import { adminTagFilterChipSx } from "./adminTagFilterChipSx";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React from "react";
import ColorService from "../../../services/ColorService";

const ZERO_STYLE = { fontWeight: 700, fontSize: "0.875rem", color: "error.main" };

const TYPE_DISPLAY_LABELS = {
    "WORD_SEARCH(mini)": "Word Search Mini",
    "WORD_SEARCH(9x9)": "Word Search Mini",
    "WORD_SEARCH(12x12)": "Word Search 12×12",
    HIDDEN_MESSAGE: "Hidden Message",
};

/**
 * Single state stats card for admin puzzle boards (cross/grid).
 * Shows total count and E/M/H difficulty counts, Free/Paid by type, and countByTag as chips.
 * When a number is 0, it is shown bold, slightly larger, and in red.
 *
 * @param {string} state - State key (e.g. DRAFT, READY_TO_REVIEW)
 * @param {Object} stateData - { countByDifficulty, countByType, countByTag?: { [tag]: number } }
 * @param {string[]} typesFilter - Types to show in countByType
 * @param {string} [stateLabelOverride] - Optional label (e.g. for merged "Ready to review / Reviewed / Published")
 * @param {boolean} [showTags=true] - Whether to show countByTag chips
 * @param {boolean} [tagsOnly=false] - When true, show only the card title and tag chips (no total, difficulty, or type counts)
 * @param {string[]} [excludeTags=[]] - Tag names to exclude from chips (e.g. ['bedrock'])
 * @param {Object} [gridSize] - Grid size override (e.g. { xs: 12, sm: 6 } for 50% width each)
 * @param {function(string)} [onTagClick] - When a tag chip is clicked, called with the tag name (e.g. to set filter)
 * @param {string} [hint] - Optional hint text shown with info icon (e.g. for tags-only card)
 * @param {boolean} [showFreePaidBreakdown=true] - When false, show total per type instead of Free/Paid split
 * @param {boolean} [showCountByType=true] - When false, hide the countByType section (e.g. single-type boards in non-published states)
 * @param {boolean} [hideTypeLabels=false] - When true, show Free/Paid counts without type label prefix
 */
export default function StateStatsCard({ state, stateData, typesFilter = [], stateLabelOverride, showTags = true, tagsOnly = false, excludeTags = [], gridSize, onTagClick, hint, showFreePaidBreakdown = true, showCountByType = true, hideTypeLabels = false }) {
    if (!stateData) return null;

    const countByDifficulty = stateData.countByDifficulty || {};
    const countByType = stateData.countByType || {};
    const countByTag = stateData.countByTag || {};
    const total = countByDifficulty.total ?? 0;
    const stateLabel = stateLabelOverride ?? state.replace(/_/g, " ");
    const stateColor = ColorService.getStateColor?.(state.toLowerCase()) || "#666";
    const excludeSet = new Set((excludeTags || []).map((t) => String(t).toLowerCase().trim()));
    const tagEntries = Object.entries(countByTag)
        .filter(([tag, n]) => Number(n) > 0 && !excludeSet.has(String(tag).toLowerCase()))
        .sort((a, b) => (a[1] ?? 0) - (b[1] ?? 0));

    const size = gridSize ?? { xs: 12, sm: 6, md: 3 };
    return (
        <Grid size={size} key={state}>
            <Card
                variant="outlined"
                sx={{
                    borderLeft: "4px solid",
                    borderLeftColor: stateColor,
                    height: "100%"
                }}
            >
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            mb: tagsOnly ? 0.5 : 1,
                            textTransform: stateLabelOverride ? "uppercase" : "capitalize"
                        }}
                    >
                        {stateLabel}
                    </Typography>
                    {hint && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                            <Tooltip title={hint}>
                                <InfoOutlinedIcon sx={{ fontSize: 18, color: "success.main" }} />
                            </Tooltip>
                            <Typography variant="caption" sx={{ flex: 1, color: "success.dark" }}>
                                {hint}
                            </Typography>
                        </Box>
                    )}
                    {!tagsOnly && (
                        <>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: total === 0 ? 700 : 600,
                                        fontSize: total === 0 ? "1.35rem" : undefined,
                                        color: total === 0 ? "error.main" : undefined
                                    }}
                                >
                                    {total}
                                </Typography>
                                {countByDifficulty.EASY > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        E:{countByDifficulty.EASY}
                                    </Typography>
                                )}
                                {countByDifficulty.MEDIUM > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        M:{countByDifficulty.MEDIUM}
                                    </Typography>
                                )}
                                {countByDifficulty.HARD > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        H:{countByDifficulty.HARD}
                                    </Typography>
                                )}
                            </Box>
                            {showCountByType && Object.keys(countByType).length > 0 && typesFilter.length > 0 && (
                                <Box sx={{ mt: 0.5 }}>
                                    {Object.entries(countByType)
                                        .filter(([type]) => typesFilter.includes(type))
                                        .map(([type, freePaid]) => {
                                            const freeCount = typeof freePaid === "object" ? (freePaid.FREE ?? 0) : 0;
                                            const paidCount = typeof freePaid === "object" ? (freePaid.PAID ?? 0) : 0;
                                            const total = typeof freePaid === "object" ? freeCount + paidCount : (Number(freePaid) || 0);
                                            const numStyle = { fontWeight: 600 };
                                            return (
                                                <Box key={type} sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                                    {!hideTypeLabels && `${TYPE_DISPLAY_LABELS[type] || type}: `}
                                                    {showFreePaidBreakdown && typeof freePaid === "object" ? (
                                                        <>
                                                            <Box component="span" sx={freeCount === 0 ? ZERO_STYLE : numStyle}>
                                                                Free {freeCount}
                                                            </Box>
                                                            {" / "}
                                                            <Box component="span" sx={paidCount === 0 ? ZERO_STYLE : numStyle}>
                                                                Paid {paidCount}
                                                            </Box>
                                                        </>
                                                    ) : (
                                                        <Box component="span" sx={total === 0 ? ZERO_STYLE : numStyle}>
                                                            {total}
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                </Box>
                            )}
                        </>
                    )}
                    {showTags && tagEntries.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                            {tagEntries.map(([tag, count]) => (
                                <Chip
                                    key={tag}
                                    label={`${tag} ${count}`}
                                    variant="outlined"
                                    onClick={onTagClick ? () => onTagClick(tag) : undefined}
                                    sx={
                                        onTagClick
                                            ? { ...adminTagFilterChipSx, cursor: "pointer" }
                                            : adminTagFilterChipSx
                                    }
                                />
                            ))}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
}
